import { Link as RouterLink } from 'react-router';
import { UserRoundSearch } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useOptionalWorkspace } from '../../hooks/useWorkspace';
import { SPECIALIST_TOPIC_LABELS, specialistPath, type SpecialistTopic } from './specialists';

export interface SpecialistLinkProps {
  /** What the decision is about. Required wherever trusts, annuities, investments, selling property or equity release come up (R-5.1). */
  topic: SpecialistTopic;
  /** `inline` = a single link; `card` (default) = a short boxed prompt. */
  variant?: 'card' | 'inline';
  className?: string;
}

/**
 * "Speak to a specialist" prompt (R-5.1). Seventide never recommends a course of action for these decisions;
 * it points to the kinds of regulated professionals who can.
 */
export function SpecialistLink({ topic, variant = 'card', className }: SpecialistLinkProps) {
  const ws = useOptionalWorkspace();
  const to = specialistPath(ws?.wsId, topic);
  const link = (
    <RouterLink to={to} className="inline-flex min-h-11 items-center gap-2 font-bold text-primary underline underline-offset-4">
      <UserRoundSearch aria-hidden="true" className="h-5 w-5" />
      Speak to a specialist<span className="sr-only"> about {SPECIALIST_TOPIC_LABELS[topic].toLowerCase()}</span>
    </RouterLink>
  );
  if (variant === 'inline') return <span className={className}>{link}</span>;
  return (
    <aside className={cn('rounded-lg border border-border bg-surface-muted p-3', className)} aria-label="Specialist help">
      <p className="text-muted">
        Decisions about {SPECIALIST_TOPIC_LABELS[topic].toLowerCase()} depend on personal circumstances. Seventide
        can&apos;t advise you on this.
      </p>
      {link}
    </aside>
  );
}
